import { useEffect, useRef } from 'react'
import { useWorkspaceStore } from '@/store/workspaceStore'
import { fetchThreadMessages, isThreadsApiAvailable } from '@/services/chat/threadsApi'
import { applyFetchedThreadMessages } from './applyFetchedThreadMessages'
import { scheduleUnresolvedFetchRetry } from './scheduleUnresolvedFetchRetry'
import { shouldFetchThreadMessages } from './shouldFetchThreadMessages'

/** Load messages for the open thread from `GET /v1/conversations/:id`. */
export function useBackendThreadMessages(model) {
  const workspaceId = useWorkspaceStore((s) => s.activeWorkspaceId)
  const loadedMessagesRef = useRef(new Set())
  const workspaceRef = useRef(workspaceId)
  const { displayThreadId, messagesByThreadId, setMessagesByThreadId } = model

  useEffect(() => {
    if (workspaceRef.current !== workspaceId) {
      workspaceRef.current = workspaceId
      loadedMessagesRef.current = new Set()
    }
  }, [workspaceId])

  useEffect(() => {
    const id = displayThreadId
    if (!id || !isThreadsApiAvailable()) return undefined
    const localMessages = messagesByThreadId[id] ?? []
    const decision = shouldFetchThreadMessages(id, localMessages, loadedMessagesRef.current)
    if (!decision.fetch) return undefined
    loadedMessagesRef.current.add(id)
    const controller = new AbortController()
    let retryTimer = null
    fetchThreadMessages(id, { signal: controller.signal })
      .then((messages) => {
        setMessagesByThreadId((prev) =>
          applyFetchedThreadMessages(prev, id, messages, decision.unresolved),
        )
        if (decision.unresolved) retryTimer = scheduleUnresolvedFetchRetry(id, setMessagesByThreadId)
      })
      .catch(() => {
        loadedMessagesRef.current.delete(id)
      })
    return () => {
      controller.abort()
      if (retryTimer) window.clearTimeout(retryTimer)
    }
  }, [displayThreadId, messagesByThreadId, setMessagesByThreadId, workspaceId])
}
